import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import picsioConfig from '../../../../../../../config';
import { showDialog, showErrorDialog } from '../../../../components/dialog';
import { setMobileMainScreenPanel } from '../../../../store/actions/main';
import Item from '../../../../components/InboxesTree/Item';
import { create } from '../../../../store/inboxes/create';
import Logger from '../../../../services/Logger';
import l10n from '../../../../shared/strings';
import { getCollectionNameValidationError } from '../../../collectionName';
import { decodeSlash } from '../../../../shared/utils';
import ua from '../../../../ua';
import { setSearchRoute } from '../../../history';

const normalizeName = (name) => decodeSlash(name.trim().toLowerCase());

function useInboxesTreeLogic({ initiatedFrom }) {
  const {
    inboxesLimit, inboxesCount, obsolete, planId,
  } = useSelector((state) => state.user.subscriptionFeatures);
  const canManageBilling = useSelector((state) => state.user.role.permissions.manageBilling);
  const fullInboxAccess = useSelector((state) => state.user.team?.featureFlags?.inboxFullAccess);
  const inboxes = useSelector((state) => state.inboxes.inboxes);
  const filtredInboxes = useSelector((state) => state.inboxes.filtredInboxes);
  const selected = useSelector((state) => (state.inboxes.activeInboxID ? [state.inboxes.activeInboxID] : []));
  const user = useSelector((state) => state.user);

  const dispatch = useDispatch();
  const [isValidNewName, setValidNewName] = useState(true);
  const [addInProgress, setAddInProgress] = useState(false);
  const [defaultNewCollectionName, setDefaultNewCollectionName] = useState('New inbox');
  const [newCollectionName, setNewCollectionName] = useState('');

  const makeNewCollectionName = useCallback(() => {
    let newName = 'New inbox';

    if (inboxes.length > 0) {
      // eslint-disable-next-line no-plusplus
      for (let i = 1; i < 10000; i++) {
        const testName = i !== 1 ? `${newName} ${i}` : newName;
        if (inboxes.every((item) => decodeSlash(item.name) !== decodeSlash(testName))) {
          newName = testName;
          break;
        }
      }
    }

    /* if new name is in progress */
    if (newName === newCollectionName) {
      const nameArr = newName.split(' ');
      const lastNumber = Number(nameArr[nameArr.length - 1]);
      if (Number.isNaN(lastNumber)) {
        newName += ' 1';
      } else {
        nameArr[nameArr.length - 1] += 1;
        newName = nameArr.join(' ');
      }
    }

    return newName;
  }, [inboxes, newCollectionName]);

  const showInboxRequestFullAccessDialog = useCallback((initiationLocation) => {
    Logger.log('User', 'InboxLimitsTriggered', {
      trigger_source: 'create_inbox_limit_reached',
      user_role: user?.role?.name,
    });
    showDialog({
      title: l10n.INBOXESTREE.requestFullDialog.title,
      text: l10n.INBOXESTREE.requestFullDialog.text(inboxesLimit),
      textBtnOk: l10n.INBOXESTREE.requestFullDialog.ok,
      textBtnCancel: l10n.INBOXESTREE.requestFullDialog.cancel,
      onOk: async () => {
        Logger.log('User', 'InboxRequestFullAccessModalChatClicked');
        await window.Intercom('showNewMessage');
        setTimeout(() => {
          window.Intercom('private:sendMessage',
            'Hi, I’d like to get full access to the Inboxes feature in Toriut.com. What’s the process to activate it?');
        }, 2000);
      },
      onCancel: () => Logger.log('User', 'InboxRequestFullAccessModalClosed'),
    });
    Logger.log('User', 'InboxRequestFullAccessModalShown', {
      initiation_location: initiationLocation,
      user_role: user?.role?.name,
    });
  }, [inboxesLimit, user?.role?.name]);

  const handleClickCreate = useCallback((e) => {
    Logger.log('User', 'InboxesPanelAddInbox', { initiatedFrom });
    if (picsioConfig.isPim && inboxesLimit <= inboxesCount) {
      showInboxRequestFullAccessDialog('Create inbox button');
      return;
    }
    e.stopPropagation();
    setAddInProgress(true);
    setDefaultNewCollectionName(makeNewCollectionName());
  }, [inboxesLimit, inboxesCount, makeNewCollectionName, showInboxRequestFullAccessDialog]);

  const handleNodeSelect = useCallback((_, { nodeId }) => {
    Logger.log('User', 'InboxesPanelSelectInbox', nodeId);
    setSearchRoute({ inboxId: nodeId });
    /** hide tree */
    if (ua.isMobileApp() || (ua.browser.isNotDesktop() && window.innerWidth < 1024)) {
      dispatch(setMobileMainScreenPanel('catalog'));
    }
  }, [dispatch]);

  const editValidation = useCallback((value) => {
    const charError = getCollectionNameValidationError(value);
    if (charError) {
      setValidNewName(false);
      return charError;
    }

    const names = inboxes.map(({ name }) => normalizeName(name));
    if (!value || names.includes(normalizeName(value))) {
      setValidNewName(false);
      return false;
    }

    setValidNewName(true);
    return true;
  }, [inboxes]);

  const handleCreate = useCallback(async (_value) => {
    setAddInProgress(true);

    const value = _value.trim();

    if (!value) {
      setValidNewName(true);
      return;
    }

    Logger.log('User', 'InboxesPanelAddInboxConfirm');

    if (value.length >= 499) {
      showDialog({
        title: l10n.DIALOGS.WARNING_COLLECTION_NAME_LENGTH.TITLE,
        text: l10n.DIALOGS.WARNING_COLLECTION_NAME_LENGTH.TEXT,
        textBtnOk: l10n.DIALOGS.WARNING_COLLECTION_NAME_LENGTH.OK_TEXT,
        textBtnCancel: null,
      });
      setAddInProgress(false);
      return;
    }

    const isTrialUser = planId === 'trial';

    const countAfterCreate = inboxesCount + 1;
    if (picsioConfig.isPim && countAfterCreate > inboxesLimit) {
      showInboxRequestFullAccessDialog('Create inbox confirm');
      setNewCollectionName('');
      setAddInProgress(false);
      return;
    }
    if (countAfterCreate > inboxesLimit && !isTrialUser) {
      /** START @TODO  Need to remove condition "obsolete" when there are no OLD PLAN users */
      if (obsolete || ua.isMobileApp()) {
        showErrorDialog(l10n.INBOXESTREE.errorHaveReachedLimit);

        setNewCollectionName('');
        setAddInProgress(false);
        return;
      }
      /** END @TODO */

      if (!canManageBilling) {
        showDialog({
          title: l10n.DIALOGS.ADD_ADDON_NOT_PERMISSION.TITLE,
          text: l10n.DIALOGS.ADD_ADDON_NOT_PERMISSION.TEXT,
          textBtnOk: l10n.DIALOGS.ADD_ADDON_NOT_PERMISSION.BTN_OK,
          textBtnCancel: null,
        });
        setNewCollectionName('');
        setAddInProgress(false);
        return;
      }

      Logger.log('User', 'UpdateInboxAddonSubscriptionModalShown');
      showDialog({
        title: l10n.DIALOGS.WARNING_ADDON_UPGRADE.TITLE,
        text: l10n.DIALOGS.WARNING_ADDON_UPGRADE.TEXT('inbox', 1),
        icon: 'notification',
        textBtnOk: l10n.DIALOGS.WARNING_ADDON_UPGRADE.OK,
        textBtnCancel: l10n.DIALOGS.WARNING_ADDON_UPGRADE.CANCEL,
        className: 'inboxesLimitUpgradeDialog',
        onOk: async () => {
          setNewCollectionName(value);
          await dispatch(create({ name: value, force: true }));
          setNewCollectionName('');
          setAddInProgress(false);
          Logger.log('User', 'UpdateInboxAddonSubscriptionModalUpdateClicked');
        },
        onCancel: () => {
          setNewCollectionName('');
          setAddInProgress(false);
          Logger.log('User', 'UpdateInboxAddonSubscriptionModalClosed');
        },
      });
    } else {
      setNewCollectionName(value);
      await dispatch(create({ name: value }));

      setNewCollectionName('');
      setAddInProgress(false);
    }
  }, [canManageBilling, dispatch, inboxesCount, inboxesLimit, obsolete, planId, showInboxRequestFullAccessDialog]);

  const handleCreateCancel = useCallback(() => {
    setAddInProgress(false);
    Logger.log('User', 'InboxesPanelAddInboxCancel');
  }, [setAddInProgress]);

  const renderNode = useCallback((props) => (
    <Item
      key={props.nodeId}
      assetsUploadedPerMonth={props.node?.assetsUploadedPerMonth || 0}
      fullInboxAccess={picsioConfig.isPim ? fullInboxAccess : true}
      alreadyUsedNames={inboxes.map(({ name }) => normalizeName(name))}
      {...props}
    />
  ), [inboxes, fullInboxAccess]);

  return {
    isValidNewName,
    addInProgress,
    newCollectionName,
    defaultNewCollectionName,
    editValidation,
    handleClickCreate,
    handleCreate,
    handleCreateCancel,
    handleNodeSelect,
    renderNode,
    inboxes,
    filtredInboxes,
    selected,
  };
}

export default useInboxesTreeLogic;
